import axios from 'axios';
import { getToken, logout, isTokenExpired } from './auth';

const DEFAULT_API_URL = 'http://localhost:62449/api';

// Create axios instance
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || DEFAULT_API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

const isAuthRoute = (url = '') => {
  // url is typically the path portion like "/login" when using axios instance baseURL
  return url === '/login' || url === '/verify-token' || url === '/logout' || url === '/change-password';
};

const normalizeAxiosError = (error) => {
  const statusCode = error?.response?.status;
  const data = error?.response?.data;

  if (typeof data === 'string') {
    return { message: data || 'Request failed', statusCode, data };
  }

  if (data && typeof data === 'object') {
    const statusSuffix = statusCode ? ` (${statusCode})` : '';
    const message = data.message || data.error || `Request failed${statusSuffix}`;
    return {
      message,
      statusCode: data.statusCode || statusCode,
      data
    };
  }

  return {
    message: error?.message || 'Network error. Please check your connection and try again.',
    statusCode,
    data: null
  };
};

/**
 * Request Interceptor - Add token to every request
 */
API.interceptors.request.use(
  (config) => {
    const token = getToken();

    if (token) {
      // Check if token is expired
      if (isTokenExpired(token)) {
        logout();
	        globalThis.location.href = '/login';
        return Promise.reject(new Error('Token expired'));
      }
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor - Handle errors globally
 */
API.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    const requestUrl = error?.config?.url || '';
    const normalized = normalizeAxiosError(error);

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // Don't hard-redirect for auth routes; let the page show the server message.
      if (!isAuthRoute(requestUrl)) {
        logout();
	        globalThis.location.href = '/login';
      }
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      console.error('Access Forbidden:', error.response.data.message);
    }

    // Handle 404 Not Found
    if (error.response?.status === 404) {
      console.error('Resource not found:', error.response.data.message);
    }

    // Handle 500 Server Error
    if (error.response?.status === 500) {
      console.error('Server error:', error.response.data.message);
    }

	    const wrapped = new Error(normalized.message || 'Request failed');
	    wrapped.statusCode = normalized.statusCode;
	    wrapped.data = normalized.data;
	    wrapped.details = normalized;
	    return Promise.reject(wrapped);
	  }
	);

/**
 * ==================== AUTH ENDPOINTS ====================
 */

export const authAPI = {
  /**
   * User Login
   */
  login: (email, password) => {
    return API.post('/login', { email, password });
  },

  /**
   * Verify Token
   */
  verifyToken: () => {
    return API.get('/verify-token');
  },

  /**
   * User Logout
   */
  logout: () => {
    return API.post('/logout');
  },

  /**
   * Change Password
   */
  changePassword: (oldPassword, newPassword) => {
    return API.post('/change-password', { oldPassword, newPassword });
  }
};

/**
 * ==================== USER ENDPOINTS ====================
 */

export const userAPI = {
  /**
   * Get all users
   */
  getAllUsers: (page = 1, limit = 10) => {
    return API.get('/users', { params: { page, limit } });
  },

  /**
   * Get user by ID
   */
  getUserById: (id) => {
    return API.get(`/users/${id}`);
  },

  /**
   * Get user by email
   */
  getUserByEmail: (email) => {
    return API.get(`/users/email/${email}`);
  },

  /**
   * Create user
   */
  createUser: (userData) => {
    return API.post('/users', userData);
  },

  /**
   * Update user
   */
  updateUser: (id, userData) => {
    return API.put(`/users/${id}`, userData);
  },

  /**
   * Delete user
   */
  deleteUser: (id) => {
    return API.delete(`/users/${id}`);
  }
};

/**
 * ==================== FILE ENDPOINTS ====================
 */

export const fileAPI = {
  /**
   * Upload file
   */
  uploadFile: (formData, config = {}) => {
    return API.post('/files/upload', formData, {
      timeout: 60000,
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      ...config
    });
  },

  /**
   * Get all files
   */
  getAllFiles: () => {
    return API.get('/files');
  },

  /**
   * Get file by ID
   */
  getFileById: (id) => {
    return API.get(`/files/${id}`);
  },

  /**
   * Get rendered data for a file (Finance only)
   */
  getRenderedData: (id) => {
    return API.get(`/files/${id}/rendered-data`);
  },

  /**
   * Get finance dashboard metrics (Excel-derived values)
   */
  getFinanceDashboardMetrics: (params = {}) => {
    return API.get('/files/dashboard/finance-metrics', { params });
  },

  /**
   * Get actional BG details table rows for dashboard
   */
  getActionalBgDetails: (params = {}) => {
    return API.get('/files/dashboard/actional-bg-details', { params });
  },

  /**
   * Create actional BG details row
   */
  createActionalBgDetail: (payload = {}) => {
    return API.post('/files/dashboard/actional-bg-details', payload);
  },

  /**
   * Get BG Data Final List table rows for dashboard
   */
  getBgDataFinalList: (params = {}) => {
    return API.get('/files/dashboard/bg-data-final-list', { params });
  },

  /**
   * Create BG Data Final List row
   */
  createBgDataFinalListRow: (payload = {}) => {
    return API.post('/files/dashboard/bg-data-final-list', payload);
  },

  /**
   * Update / delete a BG Data Final List row (ADMIN only)
   */
  updateBgDataFinalListRow: (sNo, payload = {}) => {
    return API.put(`/files/dashboard/bg-data-final-list/${encodeURIComponent(sNo)}`, payload);
  },
  deleteBgDataFinalListRow: (sNo, departmentId) => {
    return API.delete(`/files/dashboard/bg-data-final-list/${encodeURIComponent(sNo)}`, { params: { departmentId } });
  },
  importBgDataFinalListExcel: (formData) => {
    return API.post('/files/dashboard/bg-data-final-list/import-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000
    });
  },

  /**
   * BG Data Final List document attachments (5 generic "Doc N" slots per row)
   */
  listBgDataAttachments: (departmentId) => {
    return API.get('/files/dashboard/bg-data-final-list/attachments', { params: { departmentId } });
  },
  uploadBgDataAttachment: (sNo, slot, formData, departmentId) => {
    formData.append('departmentId', departmentId);
    return API.post(
      `/files/dashboard/bg-data-final-list/${encodeURIComponent(sNo)}/attachments/${encodeURIComponent(slot)}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
    );
  },
  downloadBgDataAttachment: (id, departmentId) => {
    return API.get(`/files/dashboard/bg-data-final-list/attachments/${encodeURIComponent(id)}/download`, {
      params: { departmentId },
      responseType: 'blob',
      timeout: 120000
    });
  },
  deleteBgDataAttachment: (id, departmentId) => {
    return API.delete(`/files/dashboard/bg-data-final-list/attachments/${encodeURIComponent(id)}`, { params: { departmentId } });
  },

  /**
   * Get Cash EMD list table rows for dashboard
   */
  getCashEmdList: (params = {}) => {
    return API.get('/files/dashboard/cash-emd-list', { params });
  },

  /**
   * Create Cash EMD row
   */
  createCashEmdRow: (payload = {}) => {
    return API.post('/files/dashboard/cash-emd-list', payload);
  },

  /**
   * Get Claim MIS list rows for dashboard
   */
  getClaimMisList: (params = {}) => {
    return API.get('/files/dashboard/claim-mis-list', { params });
  },

  /**
   * Create Claim MIS row
   */
  createClaimMisRow: (payload = {}) => {
    return API.post('/files/dashboard/claim-mis-list', payload);
  },

  /**
   * Update Claim MIS row (ADMIN only)
   */
  updateClaimMisRow: (id, payload = {}) => {
    return API.put(`/files/dashboard/claim-mis-list/${id}`, payload);
  },

  /**
   * Delete Claim MIS row (ADMIN only)
   */
  deleteClaimMisRow: (id) => {
    return API.delete(`/files/dashboard/claim-mis-list/${id}`);
  },

  /**
   * Get Insurance MIS list rows for dashboard
   */
  getInsuranceMisList: (params = {}) => {
    return API.get('/files/dashboard/insurance-mis-list', { params });
  },

  /**
   * Create Insurance MIS row
   */
  createInsuranceMisRow: (payload = {}) => {
    return API.post('/files/dashboard/insurance-mis-list', payload);
  },

  /**
   * Deactivate Insurance MIS row (ADMIN only)
   */
  deactivateInsuranceMisRow: (id, payload = {}) => {
    return API.post(`/files/dashboard/insurance-mis-list/${id}/deactivate`, payload);
  },

  /**
   * Activate Insurance MIS row (ADMIN only)
   */
  activateInsuranceMisRow: (id, payload = {}) => {
    return API.post(`/files/dashboard/insurance-mis-list/${id}/activate`, payload);
  },

  /**
   * Get Insurance MIS history timeline
   */
  getInsuranceMisHistory: (params = {}) => {
    return API.get('/files/dashboard/insurance-mis-list/history', { params });
  },

  /**
   * Update Insurance MIS row (ADMIN and USER)
   */
  updateInsuranceMisRow: (id, payload = {}) => {
    return API.put(`/files/dashboard/insurance-mis-list/${id}`, payload);
  },

  /**
   * Delete Insurance MIS row (ADMIN only)
   */
  deleteInsuranceMisRow: (id) => {
    return API.delete(`/files/dashboard/insurance-mis-list/${id}`);
  },

  /**
   * Send Insurance MIS expiry alert mail (next 30 days)
   */
  sendInsuranceMisExpiryAlert: (payload = {}) => {
    return API.post('/files/dashboard/insurance-mis-expiry-alert', payload);
  },

  /**
   * Get O&M attendance dashboard payload
   */
  getOmAttendanceDashboard: (params = {}) => {
    return API.get('/files/dashboard/om-attendance', { params });
  },

  /**
   * Sync O&M attendance tables from external APIs
   */
  syncOmAttendanceDashboard: (payload = {}) => {
    return API.post('/files/dashboard/om-attendance/sync', payload, { timeout: 600000 });
  },

  /**
   * Get O&M day-wise individual FRT performance
   */
  getOmFRTPerformance: (params = {}) => {
    return API.get('/files/dashboard/om-attendance/frt-performance', { params });
  },

  /**
   * Get NFS FRT & Technician dashboard payload
   */
  getNfsFrtTechnicianDashboard: (params = {}) => {
    return API.get('/files/dashboard/nfs-frt-technician', { params });
  },

  /**
   * Sync NFS FRT & Technician tables from external APIs
   */
  syncNfsFrtTechnicianDashboard: (payload = {}) => {
    return API.post('/files/dashboard/nfs-frt-technician/sync', payload, { timeout: 600000 });
  },

  /**
   * Get NFS day-wise individual FRT performance
   */
  getNfsFrtTechnicianPerformance: (params = {}) => {
    return API.get('/files/dashboard/nfs-frt-technician/frt-performance', { params });
  },

  /**
   * Cleanup NFS FRT & Technician dashboard data tables
   */
  cleanupNfsFrtTechnicianDashboard: (payload = {}) => {
    return API.post('/files/dashboard/nfs-frt-technician/cleanup', payload, { timeout: 600000 });
  },

  /**
   * Get files by project
   */
  getFilesByProject: (projectId) => {
    return API.get(`/files/project/${projectId}`);
  },

  /**
   * Get files by user
   */
  getFilesByUser: (userId) => {
    return API.get(`/files/user/${userId}`);
  },

  /**
   * Get files by type
   */
  getFilesByType: (fileType) => {
    return API.get(`/files/type/${fileType}`);
  },

  /**
   * Download file
   */
  downloadFile: (id) => {
    return API.get(`/files/${id}/download`, { responseType: 'blob' });
  },

  /**
   * Update file
   */
  updateFile: (id, data) => {
    return API.put(`/files/${id}`, data);
  },

  /**
   * Delete file (soft delete)
   */
  deleteFile: (id) => {
    return API.delete(`/files/${id}`);
  },

  /**
   * Permanently delete file
   */
  permanentlyDeleteFile: (id) => {
    return API.delete(`/files/${id}/permanent`);
  },

  /**
   * Get file statistics
   */
  getFileStatistics: () => {
    return API.get('/files/stats/summary');
  }
};

/**
 * ==================== PROJECT ENDPOINTS ====================
 */

export const projectAPI = {
  /**
   * Get all projects
   */
  getAllProjects: () => {
    return API.get('/projects');
  },

  /**
   * Get project by ID
   */
  getProjectById: (id) => {
    return API.get(`/projects/${id}`);
  },

  /**
   * Create project
   */
  createProject: (projectData) => {
    return API.post('/projects', projectData);
  },

  /**
   * Update project
   */
  updateProject: (id, projectData) => {
    return API.put(`/projects/${id}`, projectData);
  },

  /**
   * Delete project
   */
  deleteProject: (id) => {
    return API.delete(`/projects/${id}`);
  }
};

/**
 * ==================== ROLE ENDPOINTS ====================
 */

export const roleAPI = {
  /**
   * Get all roles
   */
  getAllRoles: () => {
    return API.get('/roles');
  },

  /**
   * Get role by ID
   */
  getRoleById: (id) => {
    return API.get(`/roles/${id}`);
  },

  /**
   * Create role
   */
  createRole: (roleData) => {
    return API.post('/roles', roleData);
  },

  /**
   * Update role
   */
  updateRole: (id, roleData) => {
    return API.put(`/roles/${id}`, roleData);
  },

  /**
   * Get permission IDs assigned to a role
   */
  getRolePermissions: (id) => {
    return API.get(`/roles/${id}/permissions`);
  },

  /**
   * Replace permission IDs assigned to a role
   */
  updateRolePermissions: (id, permissionIds) => {
    return API.put(`/roles/${id}/permissions`, { permissionIds });
  },

  /**
   * Delete role
   */
  deleteRole: (id) => {
    return API.delete(`/roles/${id}`);
  }
};

/**
 * ==================== DEPARTMENT ENDPOINTS ====================
 */

export const departmentAPI = {
  /**
   * Get all departments
   */
  getAllDepartments: () => {
    return API.get('/departments');
  },

  /**
   * Get department by ID
   */
  getDepartmentById: (id) => {
    return API.get(`/departments/${id}`);
  },

  /**
   * Create department
   */
  createDepartment: (deptData) => {
    return API.post('/departments', deptData);
  },

  /**
   * Update department
   */
  updateDepartment: (id, deptData) => {
    return API.put(`/departments/${id}`, deptData);
  },

  /**
   * Delete department
   */
  deleteDepartment: (id) => {
    return API.delete(`/departments/${id}`);
  }
};

/**
 * ==================== PERMISSION ENDPOINTS ====================
 */

export const permissionAPI = {
  /**
   * Get all permissions
   */
  getAllPermissions: () => {
    return API.get('/permissions');
  },

  /**
   * Get all permissions (alias)
   */
  getAll: () => {
    return API.get('/permissions');
  },

  /**
   * Get permission by ID
   */
  getPermissionById: (id) => {
    return API.get(`/permissions/${id}`);
  },

  /**
   * Create permission
   */
  createPermission: (permData) => {
    return API.post('/permissions', permData);
  },

  /**
   * Update permission
   */
  updatePermission: (id, permData) => {
    return API.put(`/permissions/${id}`, permData);
  },

  /**
   * Delete permission
   */
  deletePermission: (id) => {
    return API.delete(`/permissions/${id}`);
  }
};

/**
 * ==================== HR LEADERSHIP TM ENDPOINTS ====================
 */

export const hrLeadershipTMAPI = {
  getRecord: (department_id, parameter_id, sub_parameter_id, year_start = null) =>
    API.get('/hr-leadership-tm', { params: { department_id, parameter_id, sub_parameter_id, ...(year_start != null ? { year_start } : {}) } }),

  getParameterRecords: (department_id, parameter_id, year_start = null) =>
    API.get('/hr-leadership-tm/parameter', { params: { department_id, parameter_id, ...(year_start != null ? { year_start } : {}) } }),

  getAllRecords: (department_id, year_start = null) =>
    API.get('/hr-leadership-tm/all', { params: { department_id, ...(year_start != null ? { year_start } : {}) } }),

  upsertRecord: (payload) =>
    API.post('/hr-leadership-tm', payload),

  uploadAttachment: (formData) =>
    API.post('/hr-leadership-tm/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 }),

  downloadAttachment: (department_id, parameter_id, sub_parameter_id, attachment_index = 0, year_start = null) =>
    API.get('/hr-leadership-tm/download', { params: { department_id, parameter_id, sub_parameter_id, attachment_index, ...(year_start != null ? { year_start } : {}) }, responseType: 'blob', timeout: 30000 }),

  deleteAttachment: (payload) =>
    API.delete('/hr-leadership-tm/attachment', { data: payload }),

  rolloverToNextYear: (payload) =>
    API.post('/hr-leadership-tm/rollover', payload),
};

/**
 * ==================== HR LEARNING & DEVELOPMENT ENDPOINTS ====================
 */

export const hrLearningDevelopmentAPI = {
  getRecord: (department_id, parameter_id, sub_parameter_id, year_start = null) =>
    API.get('/hr-learning-development', { params: { department_id, parameter_id, sub_parameter_id, ...(year_start != null ? { year_start } : {}) } }),

  getParameterRecords: (department_id, parameter_id, year_start = null) =>
    API.get('/hr-learning-development/parameter', { params: { department_id, parameter_id, ...(year_start != null ? { year_start } : {}) } }),

  getAllRecords: (department_id, year_start = null) =>
    API.get('/hr-learning-development/all', { params: { department_id, ...(year_start != null ? { year_start } : {}) } }),

  upsertRecord: (payload) =>
    API.post('/hr-learning-development', payload),

  uploadAttachment: (formData) =>
    API.post('/hr-learning-development/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 }),

  downloadAttachment: (department_id, parameter_id, sub_parameter_id, attachment_index = 0, year_start = null) =>
    API.get('/hr-learning-development/download', { params: { department_id, parameter_id, sub_parameter_id, attachment_index, ...(year_start != null ? { year_start } : {}) }, responseType: 'blob', timeout: 30000 }),

  deleteAttachment: (payload) =>
    API.delete('/hr-learning-development/attachment', { data: payload }),

  rolloverToNextYear: (payload) =>
    API.post('/hr-learning-development/rollover', payload),
};

/**
 * ==================== HR DIGITAL & ENABLEMENT ENDPOINTS ====================
 */

export const hrDigitalEnablementAPI = {
  getRecord: (department_id, parameter_id, sub_parameter_id, year_start = null) =>
    API.get('/hr-digital-enablement', { params: { department_id, parameter_id, sub_parameter_id, ...(year_start != null ? { year_start } : {}) } }),

  getParameterRecords: (department_id, parameter_id, year_start = null) =>
    API.get('/hr-digital-enablement/parameter', { params: { department_id, parameter_id, ...(year_start != null ? { year_start } : {}) } }),

  getAllRecords: (department_id, year_start = null) =>
    API.get('/hr-digital-enablement/all', { params: { department_id, ...(year_start != null ? { year_start } : {}) } }),

  upsertRecord: (payload) =>
    API.post('/hr-digital-enablement', payload),

  uploadAttachment: (formData) =>
    API.post('/hr-digital-enablement/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),

  downloadAttachment: (department_id, parameter_id, sub_parameter_id, attachment_index = 0, year_start = null) =>
    API.get('/hr-digital-enablement/download', { params: { department_id, parameter_id, sub_parameter_id, attachment_index, ...(year_start != null ? { year_start } : {}) }, responseType: 'blob', timeout: 120000 }),

  downloadAttachmentById: (department_id, parameter_id, sub_parameter_id, attachment_id, year_start = null) =>
    API.get('/hr-digital-enablement/download', { params: { department_id, parameter_id, sub_parameter_id, attachment_id, ...(year_start != null ? { year_start } : {}) }, responseType: 'blob', timeout: 120000 }),

  deleteAttachment: (payload) =>
    API.delete('/hr-digital-enablement/attachment', { data: payload }),

  rolloverToNextYear: (payload) =>
    API.post('/hr-digital-enablement/rollover', payload),
};

/**
 * ==================== IT ASSET MANAGEMENT ENDPOINTS ====================
 */

export const itAssetsAPI = {
  getMyAssets: () => API.get('/it-assets/my'),
  getInventory: () => API.get('/it-assets/inventory'),
  getDashboard: () => API.get('/it-assets/dashboard'),
  searchAssetHistory: (params) => API.get('/it-assets/history', { params }),
  searchUserHistory: (params) => API.get('/it-assets/user-history', { params }),
  listAuditQueue: (params) => API.get('/it-assets/audit', { params }),
  listAssetAudits: (assetId) => API.get(`/it-assets/${encodeURIComponent(assetId)}/audits`),
  createAssetAudit: (assetId, payload) => API.post(`/it-assets/${encodeURIComponent(assetId)}/audits`, payload),
  getLookups: (domain) => API.get('/lookups', { params: { domain } }),
  // Admin lookup management
  getLookupOptions: (domain, fieldKey, includeInactive = true) => API.get('/lookups/options', { params: { domain, fieldKey, includeInactive } }),
  createLookupOption: (payload) => API.post('/lookups/options', payload),
  updateLookupOption: (id, payload) => API.patch(`/lookups/options/${encodeURIComponent(id)}`, payload),
  deleteLookupOption: (id) => API.delete(`/lookups/options/${encodeURIComponent(id)}`),
  createAsset: (payload) => API.post('/it-assets', payload),
  updateAssetMaster: (assetId, payload) => API.put(`/it-assets/${encodeURIComponent(assetId)}/master`, payload),
  importExcel: (formData) =>
    API.post('/it-assets/import-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  updateAssetCondition: (assetId, payload) => API.put(`/it-assets/${encodeURIComponent(assetId)}/condition`, payload),
  allocateAsset: (assetId, payload) => API.post(`/it-assets/${encodeURIComponent(assetId)}/allocate`, payload),
  deallocateAsset: (assetId, payload) => API.post(`/it-assets/${encodeURIComponent(assetId)}/deallocate`, payload),
  deactivateAsset: (assetId, payload) => API.post(`/it-assets/${encodeURIComponent(assetId)}/deactivate`, payload),
  deleteAsset: (assetId) => API.delete(`/it-assets/${encodeURIComponent(assetId)}`),
  getAssetHistory: (assetId) => API.get(`/it-assets/${encodeURIComponent(assetId)}/history`),
  listAttachments: (assetId, params) => API.get(`/it-assets/${encodeURIComponent(assetId)}/attachments`, { params }),
  uploadAttachment: (assetId, formData) =>
    API.post(`/it-assets/${encodeURIComponent(assetId)}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  downloadAttachment: (id) =>
    API.get(`/it-assets/attachments/${encodeURIComponent(id)}/download`, { responseType: 'blob', timeout: 120000 }),
  deleteAttachment: (id) => API.delete(`/it-assets/attachments/${encodeURIComponent(id)}`),
  createRequest: (payload) => API.post('/it-assets/requests', payload),
  listMyRequests: () => API.get('/it-assets/requests/mine'),
  listRequests: (params) => API.get('/it-assets/requests', { params }),
  approveRequest: (id) => API.post(`/it-assets/requests/${encodeURIComponent(id)}/approve`),
  rejectRequest: (id, payload) => API.post(`/it-assets/requests/${encodeURIComponent(id)}/reject`, payload),
  updateRequestPayload: (id, payload) => API.put(`/it-assets/requests/${encodeURIComponent(id)}`, payload),
};

/**
 * ==================== AUDIT LOG ENDPOINTS ====================
 */

export const auditLogAPI = {
  /**
   * Get all audit logs
   */
  getAllAuditLogs: (page = 1, limit = 20) => {
    return API.get('/audit-logs', { params: { page, limit }, timeout: 30000 });
  },

  /**
   * Get audit logs by module
   */
  getAuditLogsByModule: (module, page = 1, limit = 20) => {
    return API.get(`/audit-logs/module/${module}`, { params: { page, limit } });
  },

  /**
   * Get audit logs by user
   */
  getAuditLogsByUser: (userId, page = 1, limit = 20) => {
    return API.get(`/audit-logs/user/${userId}`, { params: { page, limit } });
  },

  /**
   * Log client-side UI event for audit trail (e.g. downloads)
   */
  logClientEvent: (payload) => {
    return API.post('/audit-logs/client-event', payload);
  }
};

/**
 * ==================== APP SETTINGS (Admin) ====================
 * Not to be confused with utils/settings.js, which is per-user localStorage
 * preferences (landing page, date format, etc). This is real server-side,
 * app-wide config — currently just Session Timeout.
 */
export const appSettingsAPI = {
  getSettings: () => {
    return API.get('/settings/app');
  },
  updateSettings: (payload) => {
    return API.put('/settings/app', payload);
  }
};

export default API;
